/**
 * documentIdentity — the open document's name and folder, for the chat's
 * setup card ("Name · …", "Folder · …").
 *
 * Neither is a chat message: the writer names and files a new document in
 * the section picker's "Save as" (``LoomFreshSelection.name`` /
 * ``folderId``), and renames / refiles it later on Home or Drafts. A
 * reopened document's selection is rebuilt without them, so the server's
 * run listing for the doc (``name`` per run, ``folder_id`` joined from the
 * session) is the current record; the live selection fills in until that
 * record has them.
 *
 * Pure: no React, no I/O.
 */

import type { ActiveRunSummary, RunBundleSummary } from "../api/client";

/** What the server says about the open document. */
export interface DocumentRecord {
  name: string | null;
  folderId: string | null;
  workspaceId: string | null;
}

/** The setup card's name / folder rows. ``name`` null = not named yet.
 *  ``folderLabel``: a label, null for Unfiled, undefined while the folder's
 *  label is still loading. */
export interface SetupDocumentInfo {
  name: string | null;
  folderLabel: string | null | undefined;
}

function stamp(run: { created_at?: string | null; completed_at?: string; started_at?: string | null }): string {
  return run.created_at ?? run.started_at ?? run.completed_at ?? "";
}

/** The newest run's name (or title) and the document's folder, or null
 *  when the document has no runs yet. */
export function documentRecordFromRuns(
  done: readonly RunBundleSummary[],
  live: readonly ActiveRunSummary[],
): DocumentRecord | null {
  const runs: Array<RunBundleSummary | ActiveRunSummary> = [...live, ...done];
  if (runs.length === 0) return null;
  const latest = [...runs].sort((a, b) => stamp(b).localeCompare(stamp(a)))[0];
  const named = latest.name?.trim() || runs.find((r) => r.name?.trim())?.name?.trim() || latest.title?.trim() || null;
  return {
    name: named,
    folderId: runs.find((r) => r.folder_id)?.folder_id ?? null,
    workspaceId: ("workspace_id" in latest ? latest.workspace_id : null) ?? null,
  };
}

/**
 * The card's name / folder: the server record when it has them, else what
 * the writer just chose in "Save as". Folder labels resolve against
 * ``folders`` (undefined while they load).
 */
export function setupDocumentInfo(
  selection: { name?: string; folderId?: string | null } | null,
  record: DocumentRecord | null,
  folders: ReadonlyArray<{ id: string; label: string }> | undefined,
): SetupDocumentInfo {
  const name = record?.name || selection?.name?.trim() || null;
  const folderId = record?.folderId ?? selection?.folderId ?? null;
  if (!folderId) return { name, folderLabel: null };
  const folder = folders?.find((f) => f.id === folderId);
  return { name, folderLabel: folder ? folder.label : undefined };
}
